import { type ReactNode, useState } from 'react';

import { OrderCardList } from '../OrderCardList/OrderCardList';

import {
  CardAction,
  CardFacts,
  type CardItem,
  CardTimeline,
  EventRows,
  type OnCardAction,
  OrderItemRows,
} from './CardParts';
import { useCardPhrases } from './CardLanguage';
import {
  asArray,
  formatCardDate,
  formatCardItemCount,
  formatCardPrice,
  formatDateWindow,
  formatOrderHeading,
  formatPostalCode,
  formatTrackingNumber,
  updatedLabel,
  visibleEvents,
} from './order-card-format';
import { orderCardLayout, type SectionState, toListCardItem } from './order-card-layout';
import type { OrderCardBlock, OrderCardData, OrderCardShipment } from './order-card-types';

import './order-card-sections.css';

function Section({
  initial,
  label,
  className,
  children,
}: {
  initial: Exclude<SectionState, null>;
  label: string;
  className: string;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(initial === 'open');
  return (
    <div className={className}>
      <button
        type="button"
        className="ai-chat__card-section-toggle"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        {label}
      </button>
      <div className="ai-chat__card-section-body" hidden={!open}>
        {children}
      </div>
    </div>
  );
}

function ShipmentEvents({
  shipment,
  withLabel,
}: {
  shipment: OrderCardShipment;
  withLabel: boolean;
}) {
  const events = visibleEvents(shipment.events);
  return (
    <>
      {withLabel && shipment.trackingNumber ? (
        <div className="ai-chat__card-shipment-label">
          {formatTrackingNumber(shipment.trackingNumber)}
        </div>
      ) : null}
      {events.length > 0 ? (
        <EventRows events={events} />
      ) : shipment.empty ? (
        // Tomma läget är normalläget de första timmarna efter skick.
        <div className="ai-chat__card-lede">
          {shipment.empty.title ? (
            <div className="ai-chat__card-status">{shipment.empty.title}</div>
          ) : null}
          {shipment.empty.body ? <div>{shipment.empty.body}</div> : null}
          {shipment.empty.eta ? <div>{shipment.empty.eta}</div> : null}
        </div>
      ) : null}
    </>
  );
}

function SingleOrderCard({
  view,
  card,
  onAction,
}: {
  view: string;
  card: OrderCardData;
  onAction?: OnCardAction;
}) {
  const phrases = useCardPhrases();
  const layout = orderCardLayout(view);
  const items = asArray<CardItem | null>(card.items).filter((item): item is CardItem =>
    Boolean(item?.name),
  );
  const itemCountLabel = formatCardItemCount(
    items.reduce((sum, item) => sum + (Number(item.quantity) || 1), 0),
    phrases,
  );
  const dateLabel = formatCardDate(card.placedAt);
  const aside = view === 'items' ? itemCountLabel || dateLabel : dateLabel;

  const shipments = asArray<OrderCardShipment | null>(card.shipments).filter(
    (shipment): shipment is OrderCardShipment => Boolean(shipment),
  );
  const factRows: Array<[string, string]> = [];
  if (layout.facts) {
    const method = String(card.delivery?.method ?? '').trim();
    if (method) factRows.push([phrases.factMethod, method]);
    const window = formatDateWindow(
      card.delivery?.windowStart,
      card.delivery?.windowEnd,
      phrases.locale,
    );
    if (window) factRows.push([phrases.factWindow, window]);
    const address = card.delivery?.address ?? {};
    const postalCity = [formatPostalCode(address.postalCode), String(address.city ?? '').trim()]
      .filter(Boolean)
      .join(' ');
    const addressLine = [String(address.street ?? '').trim(), postalCity]
      .filter(Boolean)
      .join(', ');
    if (addressLine) factRows.push([phrases.factAddress, addressLine]);
    // Ett kolli: numret hör till faktablocket. Flera: varje kolli får sitt
    // nummer som underrubrik bland händelserna i stället.
    if (shipments.length === 1 && shipments[0]?.trackingNumber) {
      factRows.push([phrases.factTracking, formatTrackingNumber(shipments[0].trackingNumber)]);
    }
  }

  const hasEvents = shipments.some(
    (shipment) => visibleEvents(shipment.events).length > 0 || shipment.empty,
  );
  const eventsLabel = [phrases.events, updatedLabel(shipments[0]?.updatedAt, Date.now(), phrases)]
    .filter(Boolean)
    .join(' · ');

  const total = Number(card.totalPrice);
  const sum =
    Number.isFinite(total) && total > 0
      ? `${phrases.total} ${formatCardPrice(total, card.currency, phrases.locale)}`
      : '';

  return (
    <article className="ai-chat__card">
      <div className="ai-chat__card-head">
        <div className="ai-chat__card-title">{formatOrderHeading(card.orderNumber, phrases)}</div>
        {aside ? <div className="ai-chat__card-aside">{aside}</div> : null}
      </div>
      {layout.timeline ? <CardTimeline timeline={card.timeline} /> : null}
      <CardFacts rows={factRows} />
      {layout.events && hasEvents ? (
        <Section initial={layout.events} label={eventsLabel} className="ai-chat__card-events">
          {shipments.map((shipment, index) => (
            <ShipmentEvents
              key={`${shipment.trackingNumber ?? ''}-${index}`}
              shipment={shipment}
              withLabel={shipments.length > 1}
            />
          ))}
        </Section>
      ) : null}
      {layout.items && items.length > 0 ? (
        view === 'items' ? (
          <div className="ai-chat__card-items">
            <OrderItemRows items={items} />
          </div>
        ) : (
          <Section initial={layout.items} label={itemCountLabel} className="ai-chat__card-items">
            <OrderItemRows items={items} />
          </Section>
        )
      ) : null}
      <div className="ai-chat__card-foot">
        <div className="ai-chat__card-sum">{sum}</div>
        {view === 'items' ? (
          <CardAction
            label={phrases.seeWholeOrder}
            value={phrases.seeWholeOrder}
            actionId="order.info"
            onAction={onAction}
          />
        ) : (
          <CardAction
            label={phrases.trackParcel}
            url={shipments[0]?.trackingUrl ?? ''}
            quietLabel={phrases.noTrackingLink}
          />
        )}
      </div>
    </article>
  );
}

// En mall för alla orderlägen: rubrik → tidslinje → fakta → händelser →
// plagg → fot. Läget avgör vad som syns och vad som är utfällt från start.
export function OrderCard({ block, onAction }: { block: OrderCardBlock; onAction: OnCardAction }) {
  if (orderCardLayout(block.view).list) {
    return (
      <OrderCardList
        orders={block.orders.map((card) => toListCardItem(block.view, card))}
        onActionClick={onAction}
      />
    );
  }
  return (
    <>
      {block.orders.map((card, index) => (
        <SingleOrderCard
          key={`${card.orderNumber}-${index}`}
          view={block.view}
          card={card}
          onAction={onAction}
        />
      ))}
    </>
  );
}
